import { MentionUnavailable, rememberTextMention } from "./MentionUnavailable";
import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Bell,
  Check,
  CheckCheck,
  Circle,
  RotateCcw,
} from "lucide-react";
import { api, notify } from "./api";
import { agentName, useAgentStyle } from "./AgentIdentity";
import { rememberReviewReceipt } from "./review-receipts";
import type { ActionItem, InboxItem } from "../shared/updates";
type Page = {
  items: (InboxItem | (ActionItem & { project: string }))[];
  cursor: string | null;
};
/** An agent's reply to your @Agent call, named in your chosen agent style. */
function AgentInboxLabel({ agent, userId }: { agent: number; userId: string }) {
  const style = useAgentStyle(userId);
  return <>{agentName(style, { id: "", agent })} replied to your @Agent request</>;
}
function localDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
export function PersonalUpdates({
  close,
  enabled,
}: {
  close: () => void;
  enabled: boolean;
}) {
  if (!enabled)
    return (
      <div className="updates-empty">
        <Bell size={22} />
        <strong>Your notifications</strong>
        <p>
          Mentions and assigned actions are available in shared cloud
          workspaces. Project edits are in Changes.
        </p>
      </div>
    );
  return <PersonalUpdatesContent close={close} />;
}
function PersonalUpdatesContent({ close }: { close: () => void }) {
  const [unavailable,setUnavailable]=useState<InboxItem|null>(null);
  const [tab, setTab] = useState<"for-you" | "actions" | "done">("for-you"),
    [project, setProject] = useState(""),
    [projects, setProjects] = useState<{ id: string; name: string }[]>([]),
    [unreadOnly, setUnreadOnly] = useState(false),
    [overdueOnly, setOverdueOnly] = useState(false),
    [data, setData] = useState<Page>({ items: [], cursor: null }),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api<{ id: string; name: string }[]>("/api/updates/projects")
      .then(setProjects)
      .catch((error) => notify((error as Error).message));
  }, []);
  const params = new URLSearchParams();
  if (project) params.set("project", project);
  if (tab === "for-you" && unreadOnly) params.set("unread", "1");
  if (tab === "actions" && overdueOnly) {
    params.set("overdue", "1");
    params.set("today", localDate());
  }
  if (tab !== "for-you") params.set("done", tab === "done" ? "1" : "0");
  const endpoint = `${tab === "for-you" ? "/api/updates" : "/api/actions"}?${params}`;
  useEffect(() => {
    let live = true;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const next = await api<Page>(endpoint);
        if (live) {
          setData(next);
          setError("");
        }
      } catch (e) {
        if (live) {
          setData({ items: [], cursor: null });
          setError((e as Error).message);
        }
      } finally {
        if (live) setLoading(false);
      }
    };
    setData({ items: [], cursor: null });
    setLoading(true);
    setError("");
    void refresh();
    const t = setInterval(refresh, 15000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [endpoint]);
  const open = async (item: Page["items"][number]) => {
    setBusy(true);
    try {
      if ("target_kind" in item && item.target_kind === "text") {
        const target = await api<{available:boolean}>(`/api/rooms/${item.board_id}/text-mentions?shape=${encodeURIComponent(item.target_id!)}`);
        if (!target.available) { setUnavailable(item); return; }
        rememberTextMention(item);
        window.dispatchEvent(new CustomEvent("planroom:open-review",{detail:{board:item.board_id,textShape:item.target_id}}));
        close(); return;
      }
      const target = await api<{
        available: boolean;
        targetMissing?: boolean;
        reason?: string;
      }>(
        `/api/rooms/${item.board_id}/review-target?thread=${encodeURIComponent(item.thread_id)}`,
      );
      if (!target.available)
        throw new Error(target.reason || "Discussion unavailable");
      if ("read_at" in item) rememberReviewReceipt(item);
      window.dispatchEvent(
        new CustomEvent("planroom:open-review", {
          detail: { board: item.board_id, thread: item.thread_id },
        }),
      );
      if (target.targetMissing)
        notify(
          "The drawing was deleted. Its discussion and action remain available.",
        );
      close();
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="updates-feed">
      {unavailable&&<MentionUnavailable item={unavailable} reason="This text was removed" close={()=>setUnavailable(null)}/>}
      <div className="updates-view-filter">
        <select
          aria-label="For you filter"
          value={tab}
          onChange={(e) => setTab(e.target.value as typeof tab)}
        >
          <option value="for-you">Mentions & replies</option>
          <option value="actions">Your actions</option>
          <option value="done">Completed actions</option>
        </select>
      </div>
      <div className="updates-filters">
        <label>
          Project
          <select
            aria-label="Filter updates by project"
            value={project}
            onChange={(event) => setProject(event.target.value)}
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        {tab === "for-you" && (
          <label>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(event) => setUnreadOnly(event.target.checked)}
            />{" "}
            Unread only
          </label>
        )}
        {tab === "actions" && (
          <label>
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(event) => setOverdueOnly(event.target.checked)}
            />{" "}
            Overdue only
          </label>
        )}
      </div>
      <div className="updates-items" aria-busy={loading}>
        {error && (
          <p className="updates-error" role="alert">
            {error}
          </p>
        )}
        {loading && (
          <p className="updates-loading" role="status">
            Loading updates…
          </p>
        )}
        {!loading && !error && !data.items.length && (
          <div className="updates-empty" role="status">
            {tab === "for-you" ? <Bell size={22} /> : <CheckCheck size={22} />}
            <strong>
              {project || unreadOnly || overdueOnly
                ? "Nothing matches these filters"
                : tab === "for-you"
                  ? "You’re all caught up"
                  : tab === "actions"
                    ? "No open actions"
                    : "No completed actions yet"}
            </strong>
            <p>
              {project || unreadOnly || overdueOnly
                ? "Change a filter to see more updates."
                : tab === "for-you"
                  ? "New mentions and replies will appear here."
                  : tab === "actions"
                    ? "Actions assigned to you will appear here."
                    : "Finished actions stay here for reference."}
            </p>
          </div>
        )}
        {data.items.map((item) => (
          <article
            key={item.id}
            className={"read_at" in item && !item.read_at ? "unread" : ""}
          >
            <div className="update-meta">
              <span title={item.project}>{item.project}</span>
              <time
                dateTime={new Date(item.updated_at).toISOString()}
                title={new Date(item.updated_at).toLocaleString()}
              >
                {new Intl.DateTimeFormat(undefined, {
                  month: "short",
                  day: "numeric",
                }).format(item.updated_at)}
              </time>
            </div>
            <button
              className="update-context"
              disabled={busy}
              onClick={() => void open(item)}
            >
              <strong>
                {"excerpt" in item && item.kind.startsWith("agent:") ? (
                  <AgentInboxLabel
                    agent={Number(item.kind.slice(6))}
                    userId={item.recipient_id ?? ""}
                  />
                ) : "excerpt" in item
                  ? `${item.actor} ${{ assignment: "assigned you an action", mention: "mentioned you", reply: "replied to a discussion" }[item.kind] ?? "updated a discussion"}`
                  : item.title}
              </strong>
              {"excerpt" in item && (
                <span className="update-excerpt">{item.excerpt}</span>
              )}
            </button>
            {!("excerpt" in item) && (
              <div className="update-status">
                <span className={item.done ? "is-complete" : "is-open"}>
                  {item.done ? <Check size={12} /> : <Circle size={10} />}
                  {item.done ? "Completed" : "Open"}
                </span>
                {item.due_date && (
                  <span
                    className={
                      !item.done && item.due_date < localDate()
                        ? "is-overdue"
                        : ""
                    }
                  >
                    {!item.done && item.due_date < localDate()
                      ? "Overdue "
                      : "Due "}
                    {new Intl.DateTimeFormat(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }).format(new Date(`${item.due_date}T00:00:00`))}
                  </span>
                )}
              </div>
            )}
            <div className="update-actions">
              <button
                className="update-open"
                disabled={busy}
                onClick={() => void open(item)}
              >
                {"excerpt" in item ? item.target_kind === "text" ? "Go to text" : "Open discussion" : "Open action"}{" "}
                <ArrowUpRight size={14} />
              </button>
              {"read_at" in item ? (
                <span className="update-read-state">
                  {item.read_at ? (
                    <>
                      <Check size={13} />
                      Seen
                    </>
                  ) : (
                    "New"
                  )}
                </span>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      await api(
                        `/api/rooms/${item.board_id}/actions/${encodeURIComponent(item.id)}`,
                        { version: item.version, done: !item.done },
                      );
                      setData((d) => ({
                        ...d,
                        items: d.items.filter((x) => x.id !== item.id),
                      }));
                    } catch (e) {
                      notify((e as Error).message);
                    }
                  }}
                >
                  {item.done ? <RotateCcw size={13} /> : <Check size={13} />}
                  {item.done ? "Reopen action" : "Complete action"}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {data.cursor && (
        <button
          onClick={async () => {
            try {
              const next = await api<Page>(
                `${endpoint}${endpoint.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(data.cursor!)}`,
              );
              setData((d) => ({
                items: [
                  ...d.items,
                  ...next.items.filter(
                    (x) => !d.items.some((y) => y.id === x.id),
                  ),
                ],
                cursor: next.cursor,
              }));
            } catch (e) {
              notify((e as Error).message);
            }
          }}
        >
          Load more
        </button>
      )}
      {tab === "for-you" && (
        <p className="updates-note">
          Seen when you view the mentioned content. Actions stay open until completed.
        </p>
      )}
    </div>
  );
}
